/**
 * Adjust event tracking.
 *
 * Adjust is attribution, not analytics: it answers "did the money we spent
 * bring this person in", so it only wants the handful of moments that matter
 * to that question. Every event also has to be created in the Adjust dashboard
 * first to get a token, which is why this is a fixed map rather than anything
 * derived from the Mixpanel event names.
 *
 * DIVISION OF LABOUR WITH REVENUECAT. Everything after the money is sent by
 * RevenueCat's own Adjust integration, server-side: trial started, trial
 * converted, initial purchase, renewals and cancellations, each carrying the
 * real amount. That is deliberately not done from here. RevenueCat knows about
 * a purchase whether or not the app was in a fit state to report it, which
 * matters because our paywall appears before signup and the app-side purchase
 * event was gated on being signed in - so most purchases were reporting
 * nothing at all.
 *
 * So this file covers only what RevenueCat cannot see: the funnel BEFORE the
 * money. Do not add purchase or revenue events here; they would double-count
 * against RevenueCat's.
 */
import { Capacitor } from '@capacitor/core';
import { Adjust, AdjustEvent } from '@awesome-cordova-plugins/adjust';

/** Event tokens from the Adjust dashboard (AppView > doqit > Events). */
export const AdjustEventToken = {
  viewedPaywall: 'ntqfud',
  completedSignUp: '9ve1h5',
  completedDocumentUpload: 'jix95g',
} as const;

/**
 * Fires an Adjust event. Safe to call from anywhere:
 *  - no-ops entirely off native, where the Adjust SDK is not initialised
 *  - never throws, because analytics must not be able to break a user flow.
 *    Every existing Adjust call site already wraps itself in try/catch for
 *    this reason; this keeps that guarantee in one place instead.
 *
 * `callbackParams` land as Adjust callback parameters, which is how anything
 * beyond the event itself has to be passed.
 */
export const trackAdjustEvent = (
  token: string,
  callbackParams?: Record<string, string | undefined>,
): void => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const event = new AdjustEvent(token);
    Object.entries(callbackParams || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        event.addCallbackParameter(key, String(value));
      }
    });
    Adjust.trackEvent(event);
  } catch (error) {
    // Swallow. An attribution event failing is never worth interrupting
    // whatever the user was actually doing.
    // eslint-disable-next-line no-console
    console.error('Adjust tracking error:', error);
  }
};
